import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SpinnerLoader from '../../../components/SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../constants/common';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ChannelColors: React.FC<ComponentProps> = ({ getChannelColors, updateChannelColors, channelColors }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draftColors, setDraftColors] = useState<Record<number, string>>({});
  const [dirtyItemNumbers, setDirtyItemNumbers] = useState<Record<number, true>>({});

  const fetchData = async (controllerId: string) => {
    setIsLoading(true);
    await getChannelColors(controllerId);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData(id);
    const intervalId = setInterval(() => fetchData(id), EXTENDED_INTERVAL_UPDATE_DATA);

    return () => clearInterval(intervalId);
  }, [id]);

  // Keep local drafts in sync with the server, but never clobber a row the user is
  // actively editing (or has unsaved changes for).
  useEffect(() => {
    setDraftColors((prev) => {
      const next = { ...prev };
      channelColors?.forEach((item) => {
        if (!dirtyItemNumbers[item.itemNumber]) {
          next[item.itemNumber] = item.color ?? '';
        }
      });
      return next;
    });
  }, [channelColors, dirtyItemNumbers]);

  const onChangeHandler = (itemNumber: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftColors((prev) => ({ ...prev, [itemNumber]: event.target.value }));
    setDirtyItemNumbers((prev) => ({ ...prev, [itemNumber]: true }));
  };

  const changedColors = useMemo(
    () =>
      (channelColors ?? []).filter(
        (item) => dirtyItemNumbers[item.itemNumber] && draftColors[item.itemNumber] !== item.color,
      ),
    [channelColors, dirtyItemNumbers, draftColors],
  );

  const onSaveAllHandler = async () => {
    if (!changedColors.length) return;

    setIsSaving(true);

    try {
      await updateChannelColors({
        controller_id: id,
        colors: changedColors.map((item) => ({ itemNumber: item.itemNumber, color: draftColors[item.itemNumber] })),
      });

      setDirtyItemNumbers((prev) => {
        const next = { ...prev };
        changedColors.forEach((item) => delete next[item.itemNumber]);
        return next;
      });

      toast.success(`${changedColors.length} channel color${changedColors.length > 1 ? 's' : ''} updated successfully`);
    } finally {
      setIsSaving(false);
    }

    await fetchData(id);
  };

  if (!channelColors?.length && isLoading) {
    return (
      <div className="p-edit-controller-channel-colors pb-4 md:pb-25">
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex h-full w-full items-center justify-center py-10">
            <SpinnerLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-edit-controller-channel-colors pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="flex items-center justify-between border-b border-stroke px-7 py-4 dark:border-strokedark">
          <div>
            <h3 className="font-medium text-black dark:text-white">Edit Channels Colors</h3>
            <p className="mt-1 text-sm text-slate-gray">
              Each channel starts with a default chart color. Change any of them here - the new color is used the
              next time this channel appears on a chart.
            </p>
          </div>
          <InteractiveButton
            text={isSaving ? 'Saving...' : 'Update All'}
            onClick={onSaveAllHandler}
            isLoading={isSaving}
            disabled={!changedColors.length}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-primary text-left text-xs uppercase leading-6 text-white">
                <th className="w-[120px] py-2 pl-7">Channel</th>
                <th className="py-2 pr-7">Color</th>
              </tr>
            </thead>
            <tbody>
              {channelColors?.map((item) => (
                <tr
                  key={item.itemNumber}
                  className="border-b border-stroke odd:bg-white even:bg-gray-2 hover:bg-stroke dark:border-strokedark"
                >
                  <td className="py-2 pl-7 font-medium text-black dark:text-white">C-{item.itemNumber}</td>
                  <td className="py-2 pr-7">
                    <span className="inline-block h-6 w-6 overflow-hidden rounded border-2 border-solid border-stroke dark:border-strokedark">
                      <input
                        className="relative inline-block h-full w-full cursor-pointer"
                        type="color"
                        value={draftColors[item.itemNumber] || '#000000'}
                        onChange={onChangeHandler(item.itemNumber)}
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  channelColors: state.channels.channelColors,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getChannelColors: dispatch.channels.getChannelColors,
  updateChannelColors: dispatch.channels.updateChannelColors,
});

export default connect(mapState, mapDispatch)(ChannelColors);
